import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import './Landing.css';

export default function MessagingOptIn({ setUserId }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [notifyPref, setNotifyPref] = useState('both');
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session_expired') === '1';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if ((notifyPref === 'sms' || notifyPref === 'both') && !smsConsent) {
      setError('Please check the SMS consent box to continue.');
      return;
    }
    try {
      const res = await fetch('/api/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, email, password, notify_pref: notifyPref })
      });
      const data = await res.json();
      if (res.status === 409) {
        setError(data.error || 'That phone number or email is already registered. Please log in instead.');
        return;
      }
      if (!res.ok) throw new Error(data.error);
      localStorage.setItem('string_user_id', data.id);
      if (setUserId) setUserId(String(data.id));
      navigate('/onboarding');
    } catch (err) {
      setError(err.message);
    }
  };

  const inputStyle = { width: '100%', padding: '0.65rem', border: '1px solid #C4B5A8', borderRadius: '6px', fontSize: '1rem', fontFamily: 'inherit', marginBottom: '1rem', background: '#FAF8F5', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontWeight: '600', color: '#5D4037', marginBottom: '0.25rem', fontSize: '0.95rem' };

  return (
    <div className="landing">
      <section className="hero" style={{ paddingBottom: '1.5rem' }}>
        <h1 className="landing-title" style={{ fontSize: '2.5rem' }}>String</h1>
        <p className="landing-subtitle">Never forget a birthday or anniversary again.</p>
      </section>

      <section style={{ maxWidth: '500px', margin: '0 auto', lineHeight: '1.8', color: '#6D6056' }}>
        <h2 style={{ color: '#5D4037', fontSize: '1.3rem', marginBottom: '1rem', textAlign: 'center' }}>Sign Up</h2>

        {sessionExpired && (
          <div style={{ background: '#FFF3E0', color: '#7B4F12', padding: '0.75rem 1rem', borderRadius: '6px', fontSize: '0.9rem', marginBottom: '1rem', textAlign: 'center' }}>
            It looks like your session expired. Please sign up again — it only takes a moment.
          </div>
        )}

        {submitted ? (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '1rem', borderRadius: '6px', textAlign: 'center' }}>
            <strong>You're all set.</strong> Head to <Link to="/onboarding" style={{ color: '#2e7d32' }}>Import Contacts</Link> to get started.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div style={{ background: '#FCEAE8', color: '#9B3B30', padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</div>}

            <label style={labelStyle}>Full Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Type your full name" required style={inputStyle} />

            <label style={labelStyle}>Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="you@example.com" style={inputStyle} />

            <label style={labelStyle}>Phone Number</label>
            <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+17165551234" required style={inputStyle} />

            <label style={labelStyle}>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required minLength={8} style={inputStyle} />
            <p style={{ fontSize: '0.8rem', color: '#9B8B80', marginTop: '-0.75rem', marginBottom: '1rem' }}>8+ characters, one number, one special character (!@#$%^&* etc.)</p>

            <label style={labelStyle}>How should we remind you?</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              {['sms', 'email', 'both'].map(pref => (
                <button
                  key={pref}
                  type="button"
                  onClick={() => {
                    if (pref === 'sms') setNotifyPref('sms');
                    else if (email) setNotifyPref(pref);
                  }}
                  style={{
                    flex: 1, fontSize: '0.85rem', padding: '0.5rem',
                    background: notifyPref === pref ? '#5D4037' : '#D7CEC7',
                    color: notifyPref === pref ? '#FAF8F5' : '#5D4037',
                    border: 'none', borderRadius: '6px', cursor: (pref !== 'sms' && !email) ? 'not-allowed' : 'pointer',
                    opacity: (pref !== 'sms' && !email) ? 0.4 : 1,
                    fontFamily: 'inherit'
                  }}
                >
                  {pref === 'sms' ? 'Text Only' : pref === 'email' ? 'Email Only' : 'Both'}
                </button>
              ))}
            </div>

            {(notifyPref === 'sms' || notifyPref === 'both') && (
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem', color: '#6D6056', lineHeight: '1.5', marginBottom: '1.25rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} style={{ width: '18px', height: '18px', marginTop: '2px', flexShrink: 0, accentColor: '#5D4037' }} />
                <span>I consent to receive non-marketing text messages from String app about birthday reminders, anniversary reminders, and event notifications. Message frequency varies. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.</span>
              </label>
            )}

            <p style={{ fontSize: '0.85rem', marginBottom: '1.25rem' }}>
              <Link to="/terms" style={{ color: '#6B7F5E', fontWeight: '500' }}>Terms of Service</Link>
              {' & '}
              <Link to="/privacy" style={{ color: '#6B7F5E', fontWeight: '500' }}>Privacy Policy</Link>
            </p>

            <button type="submit" style={{ background: '#6B7F5E', color: '#FAF8F5', border: 'none', padding: '0.7rem 1.5rem', borderRadius: '6px', fontSize: '0.95rem', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' }}>Get Started</button>

            <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#8D7B6F', textAlign: 'center' }}>
              Already have an account? <Link to="/login" style={{ color: '#6B7F5E', fontWeight: '500' }}>Log in</Link>
            </p>
          </form>
        )}

        <hr style={{ border: 'none', borderTop: '1px solid #D7CEC7', margin: '2.5rem 0' }} />

        <h2 style={{ color: '#5D4037', fontSize: '1.15rem', marginTop: '0' }}>SMS Messaging Consent</h2>

        <p><strong style={{ color: '#5D4037' }}>Program:</strong> String App</p>

        <p>Users may receive SMS messages including reminders, alerts, and responses they request from the assistant. Messages are sent only to users who have provided express consent.</p>

        <h3 style={{ color: '#5D4037', fontSize: '1rem', marginTop: '1.5rem' }}>How Users Opt In</h3>

        <p>Users provide consent by submitting their phone number through the form above and checking a required box that states:</p>

        <blockquote style={{ borderLeft: '3px solid #C4B5A8', paddingLeft: '1rem', margin: '1rem 0', fontStyle: 'italic', color: '#8D7B6F' }}>
          "I consent to receive non-marketing text messages from String app about birthday reminders, anniversary reminders, and event notifications. Message frequency varies. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out."
        </blockquote>

        <p>By checking this box and submitting the form, the user provides express consent to receive SMS messages.</p>

        <h3 style={{ color: '#5D4037', fontSize: '1rem', marginTop: '1.5rem' }}>Opting Out</h3>

        <p>Users may reply:</p>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li><strong>STOP</strong> to unsubscribe</li>
          <li><strong>HELP</strong> for assistance</li>
        </ul>

        <p>Phone numbers are used only to send requested messages and are never sold or shared with third parties.</p>

        <p style={{ marginTop: '2rem' }}>
          <strong style={{ color: '#5D4037' }}>Contact:</strong>{' '}
          <a href="mailto:reminders@stringapp.co" style={{ color: '#6B7F5E' }}>reminders@stringapp.co</a>
        </p>
      </section>

      <footer className="landing-footer" style={{ marginTop: '3rem' }}>
        <p>Like tying a string around your finger.</p>
        <p style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <Link to="/privacy" style={{ color: '#B8A99A', fontSize: '0.8rem', textDecoration: 'none' }}>Privacy Policy</Link>
          <Link to="/terms" style={{ color: '#B8A99A', fontSize: '0.8rem', textDecoration: 'none' }}>Terms of Service</Link>
        </p>
      </footer>
    </div>
  );
}
